/** @format */
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import {
    SimCardServiceTypeAllowedParamCollection,
    SimCardServiceTypeAllowedParamMap,
    SimCardServiceTypeItem,
    SimCardServiceTypeParam,
} from '../../../../interfaces/collections/SimCardServiceInterface';
import I18n from '../../../../components/Translate/I18n';
import CustomTextButtom from '../../../../components/Buttons/CustomTextButton';
import GridContainer from '../../../../components/Grid/GridContainer';
import GridItem from '../../../../components/Grid/GridItem';
import { SimpleSelect } from '../../../../components/Adescom/SimpleSelect';
import { FeatureVerbTypeMap, getFeatureVerbName } from '../../../../modules/features/FeaturesTypes';
import { usePlatformResellerHeaders, usePlatformResellerList } from '../../../../modules/lib/hooks/usePlatformResellerHooks';
import { ItemLoaders } from '../../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../../../helpers/FormGeneral';
import { OplSimCardServiceTypeParamItemBmeForm } from './OplSimCardServiceTypeParamItemBmeForm';
import Button from '@material-ui/core/Button';
import { ReactComponent as Cancel } from '../../../../assets/themes/OPL/img/icons/Cancel.svg';

const _ = require('underscore');

type OplSimCardServiceTypeParamItemBmeEditType = {
    simCardServiceType: SimCardServiceTypeItem;
    simCardServiceTypeParam: SimCardServiceTypeParam;
    simCardServiceTypeAllowedParamsCollection: SimCardServiceTypeAllowedParamCollection;
    open: boolean;
    closeCallback(): void;
    reloadList(): void;
};

export const OplSimCardServiceTypeParamItemBmeEdit: FunctionComponent<OplSimCardServiceTypeParamItemBmeEditType> = ({
    simCardServiceType,
    simCardServiceTypeParam,
    simCardServiceTypeAllowedParamsCollection,
    open,
    closeCallback,
    reloadList,
}): React.ReactElement => {
    const dispatch = useDispatch();
    const [platformResellerId, setPlatformResellerId] = useState<number | null>(simCardServiceTypeParam.platformResellerId);
    const platformResellerList = usePlatformResellerList([]);
    const platformResellerHeader = usePlatformResellerHeaders(platformResellerList);

    const [serviceParameterActivate, setServiceParameterActivate] = useState<any>(null);
    const [serviceParameterDeactivate, setServiceParameterDeactivate] = useState<any>(null);
    const [serviceParameterModify, setServiceParameterModify] = useState<any>(null);

    const confirmCallback = (): void => {
        let request: SimCardServiceTypeParam | null = null;
        if (serviceParameterActivate) {
            request = editConfiguration(serviceParameterActivate, FeatureVerbTypeMap.ACTIVATE);
        }
        if (serviceParameterDeactivate) {
            request = editConfiguration(serviceParameterDeactivate, FeatureVerbTypeMap.DEACTIVATE);
        }
        if (serviceParameterModify) {
            request = editConfiguration(serviceParameterModify, FeatureVerbTypeMap.MODIFY);
        }
        if (request) {
            const item = new ItemLoaders(dispatch);
            item.putWithCallback(
                FormGeneralHelper.getByUri(simCardServiceTypeParam['@id']),
                'put_sim_card_service_type_params_custom',
                request,
                (data: SimCardServiceTypeParam): void => {
                    reloadList();
                    closeCallback();
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    };

    /**
     * @todo change base-api to adescom-api
     * @param params
     * @param operationVerbId
     */
    const editConfiguration = (params: { [key: string]: SimCardServiceTypeAllowedParamMap }, operationVerbId: number): SimCardServiceTypeParam => {
        let configuration = { ...simCardServiceTypeParam };
        configuration.platformResellerId = platformResellerId;
        if (platformResellerId) {
            configuration.platformReseller = '/base-api/api/platform/platform_resellers/' + platformResellerId;
        }
        configuration.verb = operationVerbId;
        configuration.simCardServiceType = '/base-api/api/platform/sim_card_service_types/' + simCardServiceType.id;
        configuration.parameters = JSON.stringify(params);
        return configuration;
    };

    const setPlatformResellerCallback = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        setPlatformResellerId(parseInt(event.target.value));
    };

    const getFormValues = (operationVerbId: number, formValues: any): void => {
        if (operationVerbId === FeatureVerbTypeMap.ACTIVATE) {
            setServiceParameterActivate(formValues);
        }
        if (operationVerbId === FeatureVerbTypeMap.DEACTIVATE) {
            setServiceParameterDeactivate(formValues);
        }
        if (operationVerbId === FeatureVerbTypeMap.MODIFY) {
            setServiceParameterModify(formValues);
        }
    };

    return (
        <Dialog open={open} maxWidth="md">
            <DialogTitle>
                <GridContainer justifyContent={'space-between'}>
                    <h4>
                        <I18n trans="prov:feature.edit_configuration" />
                    </h4>
                    <Button onClick={closeCallback} color="primary">
                        <Cancel width={24} height={24} />
                    </Button>
                </GridContainer>
            </DialogTitle>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={12}>
                        <SimpleSelect
                            label="prov:feature.choose_partner"
                            required={false}
                            elements={platformResellerHeader ?? []}
                            fieldName="platformResellerId"
                            dispatchName={'none'}
                            selectedValue={platformResellerId ?? undefined}
                            callback={setPlatformResellerCallback}
                        />
                        <div style={{ fontSize: '11px' }}>
                            <I18n trans="prov:feature.no_choose_partner" />
                        </div>
                        {simCardServiceTypeAllowedParamsCollection['hydra:member'].map((item) => {
                            if (item.operationVerbId === simCardServiceTypeParam.verb) {
                                return (
                                    <React.Fragment key={'edit_params_' + item.id}>
                                        <hr />
                                        <GridContainer>
                                            <GridItem xs={3}>
                                                <Typography variant="subtitle2">
                                                    <I18n trans="prov:feature.operation_name" />
                                                </Typography>
                                            </GridItem>
                                            <GridItem xs={3}>
                                                <Typography variant="subtitle2">
                                                    <I18n trans={'prov:feature.' + getFeatureVerbName(item.operationVerbId)} />
                                                </Typography>
                                            </GridItem>
                                            <GridItem xs={6} />
                                            <GridItem xs={12}>
                                                <OplSimCardServiceTypeParamItemBmeForm
                                                    simCardServiceTypeAllowedParam={item}
                                                    simCardServiceTypeParam={simCardServiceTypeParam}
                                                    getFormValues={(formValues: any): void => getFormValues(item.operationVerbId, formValues)}
                                                />
                                            </GridItem>
                                        </GridContainer>
                                    </React.Fragment>
                                );
                            }
                        })}
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={confirmCallback} callbackParam={{}} label="prov:feature.save_change" size="sm" />
            </DialogActions>
        </Dialog>
    );
};
